// src/pages/outsourcing_management/InquiryFilterBar.tsx
//
// Filter bar for the Inquiry dashboard (EE-239), modelled on the
// Requisition dashboard's RequisitionFilterBar. Status is deliberately
// absent here - the Inquiry dashboard already owns its status chips and
// passes them as the separate `status` query param.
// Every control below maps onto an InquiryModel column that the backend's
// generic `filter` parser accepts; dropdowns are code-keyed (render the
// label, filter by the code) and the supplier is an id-keyed Autocomplete
// so a company name with a space can never break the space-separated
// token format.
//
// The controls themselves live in components/Dashboard/filterFields, shared
// with RequisitionFilterBar - see that file for why the id fields debounce.
import { FunctionComponent, useRef } from "react";
import { InquiryTypeEnum } from "types/InquiryTypes";
import { SupplierAutocomplete } from "components/Dashboard/supplierOption";
import {
  asOptions,
  ClearFiltersButton,
  FilterDateField,
  FilterIdField,
  FilterRow,
  FilterSelect,
} from "components/Dashboard/filterFields";

export type InquiryFilterState = {
  partnerId: number | undefined;
  type: string;
  deliveryMethod: string;
  orderId: string;
  quoteId: string;
  createdFrom: string;
  createdTo: string;
};

export const EMPTY_INQUIRY_FILTERS: InquiryFilterState = {
  partnerId: undefined,
  type: "",
  deliveryMethod: "",
  orderId: "",
  quoteId: "",
  createdFrom: "",
  createdTo: "",
};

// Matches the values written by the inquiry form (InquiryFields.tsx)
// and RequisitionConvertDialog - the column stores these strings verbatim.
const DELIVERY_METHODS = asOptions(["Delivery", "Pickup"]);

const INQUIRY_TYPES = asOptions(Object.values(InquiryTypeEnum));

type PropsType = {
  filters: InquiryFilterState;
  onChange: (filters: InquiryFilterState) => void;
};

const InquiryFilterBar: FunctionComponent<PropsType> = ({
  filters,
  onChange,
}) => {
  // A debounced id commit fires up to ID_DEBOUNCE_MS after the keystroke that
  // scheduled it; by then a dropdown next to it may have patched `filters`.
  // Merging onto the ref rather than the captured prop keeps that edit.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const patch = (changes: Partial<InquiryFilterState>) =>
    onChange({ ...filtersRef.current, ...changes });

  const hasActiveFilters = Object.entries(filters).some(([, value]) => !!value);

  return (
    <FilterRow>
      <FilterSelect
        label="Type"
        value={filters.type}
        onChange={(type) => patch({ type })}
        options={INQUIRY_TYPES}
      />
      <FilterSelect
        label="Delivery Method"
        value={filters.deliveryMethod}
        onChange={(deliveryMethod) => patch({ deliveryMethod })}
        options={DELIVERY_METHODS}
      />
      {/* Selects first, then Supplier, then the id fields and the date range -
          the same control order as RequisitionFilterBar (EE-238). */}
      <SupplierAutocomplete
        value={filters.partnerId}
        onChange={(partnerId) => patch({ partnerId })}
        sx={{ minWidth: 220, background: "var(--white)" }}
      />
      <FilterIdField
        label="Order ID"
        value={filters.orderId}
        onChange={(orderId) => patch({ orderId })}
      />
      <FilterIdField
        label="Quote ID"
        value={filters.quoteId}
        onChange={(quoteId) => patch({ quoteId })}
      />
      <FilterDateField
        label="Created From"
        value={filters.createdFrom}
        onChange={(createdFrom) => patch({ createdFrom })}
      />
      <FilterDateField
        label="Created To"
        value={filters.createdTo}
        onChange={(createdTo) => patch({ createdTo })}
      />
      <ClearFiltersButton
        disabled={!hasActiveFilters}
        onClick={() => onChange({ ...EMPTY_INQUIRY_FILTERS })}
      />
    </FilterRow>
  );
};

export default InquiryFilterBar;
